'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';

import { Heading } from '../elements/heading';
import { Subheading } from '../elements/subheading';
import { StrapiImage } from '@/components/ui/strapi-image';
import { jakarta } from '@/lib/fonts';
import { cn } from '@/lib/utils';

export const Brands = ({
  heading,
  sub_heading,
  logos,
}: {
  heading: string;
  sub_heading: string;
  logos: any[];
}) => {
  const middleIndex = Math.floor(logos.length / 2);
  const firstHalf = logos.slice(0, middleIndex);
  const secondHalf = logos.slice(middleIndex);
  const logosArraySplitInHalf = [firstHalf, secondHalf];

  // State to track the current logo set
  let [stateLogos, setLogos] = useState(logosArraySplitInHalf);
  const [activeLogoSet, setActiveLogoSet] = useState(stateLogos[0]);

  const flipLogos = () => {
    // Shift the logos array and update the active logo set
    setLogos((currentLogos) => {
      const newLogos = [...currentLogos.slice(1), currentLogos[0]];
      setActiveLogoSet(newLogos[0]); // Update the active set
      return newLogos;
    });
  };

  useEffect(() => {
    // Flip logos every 3 seconds
    const timer = setTimeout(() => {
      flipLogos();
    }, 3000);

    return () => clearTimeout(timer); // Clear timeout on component unmount or state update
  }, [activeLogoSet]); // Depend on activeLogoSet to trigger flip every time it changes

  return (
    <div className="relative z-20 py-16 md:py-28">
      <Heading className="pt-2">{heading}</Heading>
      <Subheading className="max-w-3xl mx-auto">{sub_heading}</Subheading>

      <div className="flex gap-8 sm:gap-12 flex-wrap justify-center md:gap-24 relative h-full w-full mt-14 max-w-6xl mx-auto px-4">
        <AnimatePresence mode="popLayout">
          {activeLogoSet.map((logo, idx) => (
            <motion.div
              initial={{
                y: 30,
                opacity: 0,
                filter: 'blur(8px)',
              }}
              animate={{
                y: 0,
                opacity: 1,
                filter: 'blur(0px)',
              }}
              exit={{
                y: -30,
                opacity: 0,
                filter: 'blur(8px)',
              }}
              transition={{
                duration: 0.7,
                delay: 0.08 * idx,
                ease: [0.4, 0, 0.2, 1],
              }}
              key={logo.title || `logo-${idx}`}
              className="relative p-3 rounded-2xl bg-white/70 dark:bg-neutral-900/60 border border-[#e5e2f5]/80 dark:border-neutral-800 shadow-sm flex items-center justify-center hover:border-[#c02029]/30 transition-colors"
            >
              <StrapiImage
                src={logo.image?.url}
                alt={logo.image.alternativeText}
                width={300}
                height={150}
                className="h-10 sm:h-12 w-32 sm:w-44 object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                draggable={false}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Brands;
